import { useEffect, useRef } from 'react';
import apiFetch from '../utils/apiFetch';
import { fetchMemberHistoryTrack, isZhxtAppMember } from '../utils/dashboard';

/**
 * 按管理后台「单兵显示天数」自动把 App 队员最近 N 天轨迹画到指挥页。
 * 每人每个天数只拉一次，避免跟随设备列表 15s 刷新重复请求。
 */
export default function useDashboardAppTrackAutoload({
  members,
  setTrackData,
  setShowTracks,
  trackDisplayDays
}) {
  const loadedRef = useRef(new Set());
  const daysRef = useRef(trackDisplayDays);

  useEffect(() => {
    if (daysRef.current !== trackDisplayDays) {
      loadedRef.current = new Set();
      daysRef.current = trackDisplayDays;
    }
  }, [trackDisplayDays]);

  useEffect(() => {
    const days = Number(trackDisplayDays) || 0;
    if (days <= 0 || !Array.isArray(members) || members.length === 0) return;

    const end = new Date();
    const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
    const pending = members.filter((m) => {
      if (!isZhxtAppMember(m) || !m.name) return false;
      const key = String(m.device_unique_id || m.name);
      return !loadedRef.current.has(key);
    });
    if (!pending.length) return;

    let cancelled = false;
    (async () => {
      for (const member of pending) {
        if (cancelled) break;
        const key = String(member.device_unique_id || member.name);
        loadedRef.current.add(key);
        const result = await fetchMemberHistoryTrack({
          name: member.name,
          trackData: {},
          forceRefresh: true,
          findMember: () => member,
          explicitRange: {
            start: start.toISOString(),
            end: end.toISOString()
          },
          apiFetch
        });
        if (cancelled) break;
        if (result.action === 'success' && result.track?.length) {
          setTrackData((prev) => ({ ...prev, [member.name]: result.track }));
          setShowTracks((prev) => ({ ...prev, [member.name]: true }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [members, trackDisplayDays, setTrackData, setShowTracks]);
}
